import { Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AppConsts } from "@shared/app-consts";
import { ReceivedInvoiceListCrmResult, AvailableActions, ReceivedInvoiceDto } from "@shared/e-invoice";
import { BaseService } from "@services/base.service";

@Injectable({
  providedIn: "root",
})
export class EInvoicePdpInboundService extends BaseService {
  private readonly baseUrl = AppConsts.API_SERVICE_URL + "EFacture/ReceivedInvoices";

  constructor(private http: HttpClient) {
    super();
  }

  getReceived(
    tenantId: string,
    siren: string,
    cabinetId: number,
    page: number = 1,
    pageSize: number = 20
  ): Observable<ReceivedInvoiceListCrmResult> {
    const httpOptions = this.getHttpHeader(false);

    const params = new HttpParams()
      .set("tenantId", tenantId)
      .set("siren", siren)
      .set("cabinetId", cabinetId.toString())
      .set("page", page.toString())
      .set("pageSize", pageSize.toString());

    return this.http.get<ReceivedInvoiceListCrmResult>(
      `${this.baseUrl}`,
      { ...httpOptions, params }
    );
  }

  getReceivedDetail(id: string, tenantId: string): Observable<ReceivedInvoiceDto> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set('tenantId', tenantId);
    return this.http.get<ReceivedInvoiceDto>(
      `${this.baseUrl}/${encodeURIComponent(id)}`,
      { ...httpOptions, params }
    );
  }

  downloadAttachment(attachmentId: string, tenantId: string): Observable<HttpResponse<Blob>> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set('tenantId', tenantId);
    return this.http.get(`${this.baseUrl}/attachments/${encodeURIComponent(attachmentId)}/download`, {
      ...httpOptions,
      params,
      responseType: 'blob',
      observe: 'response',
    }) as Observable<HttpResponse<Blob>>;
  }

  download(id: string, tenantId: string): Observable<{ blob: Blob; filename: string }> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set("tenantId", tenantId);
    return this.http.get(`${this.baseUrl}/${id}/download`, {
      ...httpOptions,
      params,
      responseType: "blob",
      observe: "response",
    }).pipe(
      map(response => {
        const cd = response.headers.get("Content-Disposition") || "";
        const filename = this.extractFilename(cd) || id;
        return { blob: response.body as Blob, filename };
      })
    );
  }

  refuse(id: string, tenantId: string, reason: number, comment: string): Observable<void> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set('tenantId', tenantId);
    const body = { comment, reason };
    return this.http.post<void>(`${this.baseUrl}/${encodeURIComponent(id)}/refuse`, body, { ...httpOptions, params });
  }

  approve(id: string, tenantId: string, comment: string): Observable<void> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set('tenantId', tenantId);
    return this.http.post<void>(`${this.baseUrl}/${encodeURIComponent(id)}/approve`, { comment }, { ...httpOptions, params });
  }

  suspend(id: string, tenantId: string, reason: number, comment: string): Observable<void> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set('tenantId', tenantId);
    return this.http.post<void>(`${this.baseUrl}/${encodeURIComponent(id)}/suspend`, { comment, reason }, { ...httpOptions, params });
  }

  sendPayment(id: string, tenantId: string, comment: string): Observable<void> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set('tenantId', tenantId);
    return this.http.post<void>(`${this.baseUrl}/${encodeURIComponent(id)}/send-payment`, { comment }, { ...httpOptions, params });
  }

  sendPartialPayment(id: string, tenantId: string, amount: number, comment: string): Observable<void> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set('tenantId', tenantId);
    return this.http.post<void>(`${this.baseUrl}/${encodeURIComponent(id)}/send-partial-payment`, { comment, amount }, { ...httpOptions, params });
  }

  getAvailableActions(id: string, tenantId: string): Observable<AvailableActions> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set('tenantId', tenantId);
    return this.http.get<AvailableActions>(`${this.baseUrl}/${encodeURIComponent(id)}/available-actions`, { ...httpOptions, params });
  }

  getLifecycle(id: string, tenantId: string): Observable<any> {
    const httpOptions = this.getHttpHeader(false);
    const params = new HttpParams().set('tenantId', tenantId);
    return this.http.get<any>(`${this.baseUrl}/${encodeURIComponent(id)}/lifecycle`, { ...httpOptions, params });
  }

  private extractFilename(contentDisposition: string): string {
    // RFC 5987 : filename*=UTF-8''...
    const starMatch = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
    if (starMatch) return decodeURIComponent(starMatch[1]);
    // Fallback : filename="..." ou filename=...
    const match = contentDisposition.match(/filename=["']?([^;"'\s]+)["']?/i);
    return match ? match[1].trim() : null;
  }}